// frontend/admin-portal/src/contexts/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { authService } from "../services/authService";
import { checkPermission } from "@fiberhome/shared-ui";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setLoading(false);
      return;
    }
    authService
      .me()
      .then(setUser)
      .catch(() => localStorage.clear())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    localStorage.setItem("accessToken", data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
    }
  };

  const permissions = useMemo(() => {
    const perms = user?.role?.permissions;
    return Array.isArray(perms) ? perms : [];
  }, [user]);

  const hasPermission = useCallback(
    (requested) => checkPermission(permissions, requested),
    [permissions],
  );
  const hasAny = useCallback(
    (list) =>
      Array.isArray(list) && list.some((p) => checkPermission(permissions, p)),
    [permissions],
  );
  const hasAll = useCallback(
    (list) =>
      Array.isArray(list) && list.every((p) => checkPermission(permissions, p)),
    [permissions],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        logout,
        permissions,
        hasPermission,
        hasAny,
        hasAll,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
